import { useNavigate } from 'react-router-dom';

import { LinkItem } from '@occc/ui-design-system';
import { useOpenState } from '@occc-app/hooks/useOpenState';
import { useGetMessagingCampaignById } from '@occc-app/dataAccessLayer/messagingCenter/queries';
import { useChainId } from '@occc-app/Chain/hooks';
import { useGetAudienceById } from '@occc-app/dataAccessLayer/audience/useGetAudienceById';
import { Flow_Types } from '@occc-app/offerHelpers/types';

import { useGetFormContainerProps } from './use-get-form-container-props.hook';

import { routeCreator } from '../../../messaging-center.utils';
import { CONFIGURE_PAGE_SUBTITLE, PAGE_TITLE } from '../../../messaging-center.constants';

type GetContainerPropsArgs = {
  campaignId?: string;
  getBreadcrumbLinks: (campaignName: string) => LinkItem[];
  flowType: Flow_Types;
};

export const useGetContainerProps = ({
  campaignId,
  getBreadcrumbLinks,
  flowType,
}: GetContainerPropsArgs) => {
  const navigate = useNavigate();
  const chainId = useChainId();
  const [open, openModal, closeModal] = useOpenState();

  const redirectToMessagingCampaignDetails = () => {
    navigate(routeCreator.campaignDetails(campaignId || ''));
  };

  const redirectToMessagingCampaignsList = () => {
    navigate(routeCreator.campaignsList());
  };

  const handleCancelClick = (hasUnsavedChanges?: boolean) => {
    hasUnsavedChanges ? openModal() : redirectToMessagingCampaignDetails();
  };

  const { data: campaign, loading: campaignLoading } = useGetMessagingCampaignById(campaignId);
  const { data: audience, loading: audienceLoading } = useGetAudienceById(
    campaign?.audienceUuid ?? undefined,
    chainId,
  );
  const campaignDetailsRoute = routeCreator.campaignDetails(campaignId || '');

  const dialogProps = {
    open,
    closeModal,
    title: 'Messaging Campaign',
    onConfirmClick:
      flowType === Flow_Types.Configure
        ? redirectToMessagingCampaignsList
        : redirectToMessagingCampaignDetails,
  };

  const { layoutProps, configureFormProps, targetFormProps, scheduleFormProps } =
    useGetFormContainerProps({
      campaign,
      audience,
      campaignDetailsRoute,
      onCancel: flowType === Flow_Types.Configure ? openModal : handleCancelClick,
      chainId,
      loading: campaignLoading || audienceLoading,
    });

  return {
    layoutProps,
    dialogProps,
    configureFormProps,
    targetFormProps,
    scheduleFormProps,
    pageTitle: campaign?.title || PAGE_TITLE,
    pageSubtitle: campaign?.description || CONFIGURE_PAGE_SUBTITLE,
    breadcrumbLinks: getBreadcrumbLinks(campaign?.title || ''),
  };
};
